import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { UseFormSetValue } from 'react-hook-form';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';

import styles from './fileUploadWidgetStyles.scss';

import LoadingSpinner from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import FileUploadList from './FileUploadList/fileUploadList';
import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';

import XMarkIcon from 'SVGs/x_mark.svg';
import {
  CreateIdeaCommentMutationVariables,
  CreateIdeaUpdateCommentMutationVariables
} from '@interfaces/graphql-typings';

export type FileType = {
  name?: string;
  file: File;
  base64: string | ArrayBuffer | null;
};

const defaultAccept: { [key: string]: string[] } = {
  'image/*': ['.jpeg', '.jpg', '.png'],
  'application/msword': ['.doc'],
  'application/pdf': ['.pdf'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/csv': ['.csv']
};

const maxLength = 255;
const verifyFilenameLength = (filename: string): boolean => filename.length <= maxLength;

type SetFieldValueType = Omit<
  CreateIdeaCommentMutationVariables | CreateIdeaUpdateCommentMutationVariables,
  'ideaId' | 'ideaUpdateId' | 'files'
> & { ideaId?: string; ideaUpdateId?: string; files: FileType[] };

const FileUploadWidget: React.FC<{
  uploadedFiles: FileType[];
  setFieldValue: UseFormSetValue<SetFieldValueType>;
  accept?: { [key: string]: string[] };
}> = ({ uploadedFiles, setFieldValue, accept }): React.ReactElement => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Call this function with a ref if you need to manually close
  // const closeWidget = () => {
  //   setUploadOpen(false);
  //   setLoading(false);
  // };

  const resetFiles = (): void => {
    setFieldValue('files', []);
  };

  const hasFiles = (): boolean => {
    return uploadedFiles && uploadedFiles.length > 0;
  };

  const handleToggleOpen = (e: Event): void => {
    e.preventDefault();
    if (uploadOpen && hasFiles()) {
      if (confirm('Closing this will remove all file attachments')) {
        resetFiles();
        setUploadOpen(false);
      }
    } else {
      setUploadOpen(!uploadOpen);
    }
  };

  const handleDrop = (currentFile: FileType): void => {
    const { file, base64 } = currentFile;

    setFieldValue('files', [...uploadedFiles, { name: '', file, base64 }]);
  };

  const handleUpload = (files: File[]): void => {
    if (loading) return;
    const file = files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = (): void => {
        handleDrop({ file, base64: reader.result });
        setLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const nameLengthValidator = (file: File): { code: string; message: string } | null => {
    if (!verifyFilenameLength(file.name)) {
      return {
        code: 'name-too-large',
        message: `Name is larger than ${maxLength} characters`
      };
    }
    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: accept || defaultAccept,
    onDrop: handleUpload,
    validator: nameLengthValidator
  });

  const deleteFile = (idx: number): void => {
    let newFiles = [...uploadedFiles];
    newFiles.splice(idx, 1);
    if (newFiles.length === 0) newFiles = [];
    setFieldValue('files', newFiles);
  };

  const changeName = (value: string, idx: number): void => {
    if (!verifyFilenameLength(value)) {
      setError('Please limit file names to fewer than 255 characters');
      return;
    }
    const newFiles = [...uploadedFiles];
    newFiles[idx] = Object.assign({}, newFiles[idx]);
    newFiles[idx].name = value;
    setFieldValue('files', newFiles);
    setError('');
  };

  if (!uploadOpen) {
    return (
      <Button
        isLoading={loading}
        onClick={handleToggleOpen}
        type={BUTTON_TYPES.LINK}
        faIcon={faPlus}
        text="Add File Attachments"
      />
    );
  }

  const acceptableExtensions = Object.values(accept || defaultAccept)
    .reduce((memo, arr) => Array.from(new Set([...memo, ...arr.map(el => el)])), [])
    .join(', ');

  return (
    <div>
      <XMarkIcon className={styles.closeButton} onClick={handleToggleOpen} />
      <div className={styles.fileUploadWrapper}>
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          <div>Drag and drop a file here, or click to select one</div>
        </div>
        <div className={styles.fileFormatCopy}>
          <strong>Allowed formats:</strong> {acceptableExtensions}
        </div>
        {loading && (
          <div className={styles.loadingArea}>
            <LoadingSpinner />
          </div>
        )}
        {hasFiles() && (
          <FileUploadList files={uploadedFiles} deleteFile={deleteFile} changeName={changeName} />
        )}
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </div>
  );
};

export default FileUploadWidget;
